import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import AiChoirPanel from "@/components/bgv/AiChoirPanel";
import BgvActions from "@/components/bgv/BgvActions";
import BgvMixer from "@/components/bgv/BgvMixer";
import BgvProgress from "@/components/bgv/BgvProgress";
import SectionsPanel from "@/components/bgv/SectionsPanel";
import SingersPanel from "@/components/bgv/SingersPanel";
import useBgvSession from "@/components/bgv/useBgvSession";
import { retryBgv } from "@/lib/bgvApi";
import { safeApiError } from "@/lib/safeErrorText";
import { clock } from "@/lib/voiceSongsApi";

function Facts({ doc }) {
  const i = doc.info || {};
  const facts = [["Lead", doc.source?.label], ["Length", i.duration ? clock(i.duration) : "—"], ["Key heard", i.key || "—"], ["Range", i.range || "—"], ["Music", i.has_music ? "Yes" : "No"]];
  return (
    <div data-testid="bgv-facts" className="mb-6 flex flex-wrap gap-2">
      {facts.map(([k, v]) => <span key={k} className="max-w-full truncate rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[12px]"><span className="text-white/45">{k}: </span>{v}</span>)}
    </div>
  );
}

/** `/create/background-singers/:id` — progress, then the mixer, singer / section choices, the AI choir and save / download. */
export default function BgvSessionPage() {
  const { id } = useParams();
  const s = useBgvSession(id);
  const [retrying, setRetrying] = useState(false);
  const { doc, settings } = s;
  const retry = async () => {
    setRetrying(true);
    try { s.adopt(await retryBgv(id)); } catch (e) { toast.error("Couldn't restart", { description: safeApiError(e) }); }
    finally { setRetrying(false); }
  };
  const ready = Boolean(doc?.info && settings);
  return (
    <CreatorShell testId="bgv-session-page" back="/create/background-singers" title={doc?.title || "Background Singers"} subtitle="Background singers — every part on its own track" wide>
      {s.error && !doc ? <p data-testid="bgv-not-found" className="text-[14px] text-rose-200">{safeApiError(s.error)}</p> : null}
      {!doc && !s.error ? <Loader2 className="h-6 w-6 animate-spin text-white/40" /> : null}
      {doc ? <Facts doc={doc} /> : null}
      {doc && (!ready || (doc.status === "error" && !doc.tracks.length)) ? <BgvProgress doc={doc} onRetry={retry} retrying={retrying} /> : null}
      {ready ? (
        <div className="space-y-6">
          {doc.status === "error" && doc.tracks.length ? <BgvProgress doc={doc} onRetry={retry} retrying={retrying} /> : null}
          {doc.tracks.length ? <BgvMixer doc={doc} settings={settings} change={s.change} saveState={s.saveState} /> : null}
          <div className="grid items-start gap-6 lg:grid-cols-[1.2fr_1fr]">
            <div className="space-y-6">
              <SingersPanel doc={doc} settings={settings} change={s.change} />
              <SectionsPanel doc={doc} settings={settings} change={s.change} />
            </div>
            <div className="space-y-6">
              <BgvActions doc={doc} settings={settings} flush={s.flush} onStarted={s.adopt} />
              <AiChoirPanel doc={doc} settings={settings} change={s.change} flush={s.flush} onStarted={s.adopt} />
            </div>
          </div>
        </div>
      ) : null}
    </CreatorShell>
  );
}
